import { Fragment } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface BottleGroupContainerIconProps {
    group: any;
    onPlaceOrder: (group: any) => void;
}

// Only groups linked to a quote analysis reference can place a container order
const BottleGroupContainerIcon = ({ group, onPlaceOrder }: BottleGroupContainerIconProps) => {
    const quoteAnalysisRefID = group?.quoteAnalysisRefID;
    const hasQuoteAnalysisRef = quoteAnalysisRefID !== null && quoteAnalysisRefID !== undefined && Number(quoteAnalysisRefID) !== 0;

    if (!hasQuoteAnalysisRef) {
        return null;
    }

    return (
        <Tooltip title={'For Your Reference'}>
            <span>
                <IconButton onClick={() => onPlaceOrder(group)}>
                    <Inventory2OutlinedIcon sx={{ color: 'primary.main' }} />
                </IconButton>
            </span>
        </Tooltip>
    );
};

interface BottleGroupFromQuoteProps {
    rows: any;
    bottleGroupRows: any[];
    bottleGroupColumns: any[];
    expandedBottleGroups: Record<string | number, boolean>;
    toggleBottleGroupExpanded: (groupKey: string | number) => void;
    resolveBottleGroupName: (group: any, rowIndex?: number) => string;
    handleBottleGroupCellChange: (idx: number, field: string, value: any) => void;
    handleBottleGroupTickToggle: (idx: number, forceMapRequested?: boolean) => void;
    isRowEditable: (row: any) => boolean;
    canEditRequestedQuantity: (row: any) => boolean;
    canAddContainers: boolean;
    commentOptions: string[];
    renderReadonlyTreeItems: (items: any[], typeLabel: string, parentKey: string | number) => any;
    handleAddContainersOpen: (type: string) => void;
    handlePlaceOrderForGroup: (group: any) => void;
}

export const BottleGroupFromQuote = ({
    rows,
    bottleGroupRows,
    bottleGroupColumns,
    expandedBottleGroups,
    toggleBottleGroupExpanded,
    resolveBottleGroupName,
    handleBottleGroupCellChange,
    handleBottleGroupTickToggle,
    isRowEditable,
    canEditRequestedQuantity,
    canAddContainers,
    commentOptions,
    renderReadonlyTreeItems,
    handleAddContainersOpen,
    handlePlaceOrderForGroup
}: BottleGroupFromQuoteProps) => {
    if (!(bottleGroupRows.length > 0)) {
        return null;
    }

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6"
                    sx={{
                        mb: 2, fontWeight: 600,
                        '@media print': {
                            color: '#000 !important',
                        },
                    }}>
                    Bottle Groups
                </Typography>
                {rows?.id && (
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={() => handleAddContainersOpen('group')}
                        disabled={!canAddContainers}
                    >
                        Add Container
                    </Button>
                )}
            </Box>
            <Card variant="outlined" className="print-table-card" sx={{ borderRadius: 1 }}>
                <CardContent sx={{ padding: '0px 0px !important' }}>
                    <div className="print-area">
                        <Table sx={{ minWidth: 700 }}>
                            <TableHead sx={{ backgroundColor: 'rgb(0, 56, 131)' }}>
                                <TableRow>
                                    {bottleGroupColumns.map((col: any) => (
                                        <TableCell className={col.field === 'action' ? 'print-hide' : undefined} key={col.field} sx={{ color: '#fff', minWidth: col.minWidth, fontWeight: 600, padding: '12px' }}>
                                            {col.field === 'select' ? (
                                                <>
                                                    <Box className="print-hide">
                                                        <Checkbox
                                                            checked={bottleGroupRows.length > 0 && bottleGroupRows.every(r => r.isCompleted)}
                                                            indeterminate={bottleGroupRows.some(r => r.isCompleted) && !bottleGroupRows.every(r => r.isCompleted)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    bottleGroupRows.forEach((_, idx) => handleBottleGroupTickToggle(idx, false));
                                                                } else {
                                                                    bottleGroupRows.forEach((group, idx) => {
                                                                        if (group.isCompleted) {
                                                                            handleBottleGroupTickToggle(idx, false);
                                                                        }
                                                                    });
                                                                }
                                                            }}
                                                            sx={{
                                                                padding: 0,
                                                                color: '#fff',
                                                                '&.Mui-checked': { color: '#fff' },
                                                                '&.MuiCheckbox-indeterminate': { color: '#fff' }
                                                            }}
                                                            disabled={rows?.status === 1 || rows?.status === 3 || rows?.status === 4}
                                                        />
                                                    </Box>
                                                    <Box className="print-status-text" sx={{ display: 'none', '@media print': { display: 'block !important' } }}>
                                                        <Typography variant="body2">&nbsp;</Typography>
                                                    </Box>
                                                </>
                                            ) : col.field === 'containerName' || col.field === 'name' ? 'Name' : col.headerName}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bottleGroupRows.map((group, idx) => {
                                    const groupFulfilledValue = group.fulfilledQuantity ?? '';
                                    const groupRequestedValue = group.requestedQuantity ?? '';
                                    const groupCommentsRequired = rows?.status === 2 && groupFulfilledValue !== '' && Number(groupFulfilledValue) !== Number(group.requestedQuantity);
                                    const groupKey = group.id ?? idx;
                                    const isExpanded = !!expandedBottleGroups[groupKey];
                                    const quoteAnalysisRefID = group?.quoteAnalysisRefID;

                                    return (
                                        <Fragment key={groupKey}>
                                            <TableRow
                                                sx={{
                                                    backgroundColor: group.isCompleted ? '#f8f9fa' : 'inherit',
                                                    '& .MuiTableCell-root': {
                                                        backgroundColor: group.isCompleted ? '#f8f9fa' : 'inherit',
                                                        fontWeight: group.isCompleted ? 600 : 'normal'
                                                    }
                                                }}
                                            >
                                                {bottleGroupColumns.map((col: any) => {
                                                    if (col.field === 'containerName' || col.field === 'name') {
                                                        const groupDisplayName = resolveBottleGroupName(group, idx);

                                                        return (
                                                            <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                <Box className="print-hide">
                                                                    <Button
                                                                        variant="text"
                                                                        onClick={() => toggleBottleGroupExpanded(groupKey)}
                                                                        sx={{
                                                                            p: 0,
                                                                            minWidth: 0,
                                                                            width: 'auto',
                                                                            textTransform: 'none',
                                                                            justifyContent: 'flex-start',
                                                                            gap: 0.5,
                                                                            overflow: 'hidden',
                                                                            color: '#0d47a1',
                                                                            textDecoration: 'underline',
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'anywhere'
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            component="span"
                                                                            noWrap
                                                                            title={groupDisplayName}
                                                                            sx={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                textAlign: 'left',
                                                                                whiteSpace: 'normal',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'anywhere'
                                                                            }}
                                                                        >
                                                                            {groupDisplayName}
                                                                        </Typography>
                                                                        <Typography component="span">
                                                                            {isExpanded ? '(-)' : '(+)'}
                                                                        </Typography>
                                                                    </Button>
                                                                    <BottleGroupContainerIcon group={group} onPlaceOrder={handlePlaceOrderForGroup} />
                                                                </Box>

                                                                <Box
                                                                    className="print-status-text"
                                                                    sx={{ display: 'none', '@media print': { display: 'block !important' } }}
                                                                >
                                                                    <Typography variant="body2"
                                                                        className="group-name-print-text"
                                                                        sx={{
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'anywhere',
                                                                            textAlign: 'left'
                                                                        }}>
                                                                        {groupDisplayName}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (col.field === 'requestedQuantity') {
                                                        return (
                                                            <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                <Box className="print-hide">
                                                                    <TextField
                                                                        size="small"
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={groupRequestedValue}
                                                                        onChange={canEditRequestedQuantity(group) && !group.requestedQuantityDisabled ? e => {
                                                                            const value = e.target.value;
                                                                            if (value === '' || /^\d*$/.test(value)) {
                                                                                handleBottleGroupCellChange(idx, 'requestedQuantity', value);
                                                                            }
                                                                        } : undefined}
                                                                        onBlur={canEditRequestedQuantity(group) && !group.requestedQuantityDisabled ? e => {
                                                                            const value = e.target.value;
                                                                            if (value === '' || isNaN(Number(value)) || Number(value) < 0) {
                                                                                handleBottleGroupCellChange(idx, 'requestedQuantity', 0);
                                                                            }
                                                                        } : undefined}
                                                                        type="text"
                                                                        inputProps={{ style: { width: 60 } }}
                                                                        InputProps={{ readOnly: !canEditRequestedQuantity(group) || group.requestedQuantityDisabled }}
                                                                        disabled={!canEditRequestedQuantity(group) || group.requestedQuantityDisabled}
                                                                    />
                                                                </Box>
                                                                <Box className="print-status-text" sx={{ display: 'none', '@media print': { display: 'block !important' } }}>
                                                                    <Typography variant="body2">
                                                                        {groupRequestedValue !== '' && groupRequestedValue !== null && groupRequestedValue !== undefined ? groupRequestedValue : '-'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (col.field === 'fulfilledQuantity') {
                                                        return (
                                                            <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                <Box className="print-hide">
                                                                    <TextField
                                                                        size="small"
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={groupFulfilledValue}
                                                                        onChange={isRowEditable(group) ? e => {
                                                                            const value = e.target.value;
                                                                            if (value === '' || /^\d*$/.test(value)) {
                                                                                handleBottleGroupCellChange(idx, 'fulfilledQuantity', value);
                                                                            }
                                                                        } : undefined}
                                                                        onBlur={isRowEditable(group) ? e => {
                                                                            const value = e.target.value;
                                                                            if (value === '' || isNaN(Number(value)) || Number(value) < 0) {
                                                                                handleBottleGroupCellChange(idx, 'fulfilledQuantity', 0);
                                                                            }
                                                                        } : undefined}
                                                                        type="text"
                                                                        inputProps={{ style: { width: 60 } }}
                                                                        InputProps={{ readOnly: !isRowEditable(group) || group.fulfilledDisabled }}
                                                                        disabled={rows?.status === 1 || rows?.status === 3 || rows?.status === 4 || group.fulfilledDisabled}
                                                                    />
                                                                </Box>
                                                                <Box className="print-status-text" sx={{ display: 'none', '@media print': { display: 'block !important' } }}>
                                                                    <Typography variant="body2">
                                                                        {groupFulfilledValue !== '' && groupFulfilledValue !== null && groupFulfilledValue !== undefined ? groupFulfilledValue : '-'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (col.field === 'comments') {                                                        

                                                        return (
                                                            <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                <Box className="print-hide">
                                                                    <TextField
                                                                        size="small"
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        select
                                                                        label="Comments"
                                                                        value={group.comments ?? ''}
                                                                        sx={{ maxWidth: { xs: '100px', sm: '116px', md: '100%' } }}
                                                                        onChange={
                                                                            isRowEditable(group)
                                                                                ? (e) => {
                                                                                    const value = e.target.value;

                                                                                    // Update the comment
                                                                                    handleBottleGroupCellChange(idx, 'comments', value);

                                                                                    // Trigger tick only when a valid comment is selected
                                                                                    if (
                                                                                        groupCommentsRequired &&
                                                                                        value &&
                                                                                        value.trim() !== ''
                                                                                    ) {
                                                                                        handleBottleGroupTickToggle(idx, false);
                                                                                    }
                                                                                }
                                                                                : undefined
                                                                        }
                                                                        InputProps={{
                                                                            readOnly: !isRowEditable(group),
                                                                        }}
                                                                        disabled={
                                                                            rows?.status !== 2 ||
                                                                            group.isCompleted ||
                                                                            !groupCommentsRequired
                                                                        }
                                                                        required={groupCommentsRequired}
                                                                        error={group.showCommentsError}
                                                                        helperText={
                                                                            group.showCommentsError
                                                                                ? 'Comments is required'
                                                                                : ''
                                                                        }
                                                                    >
                                                                        <MenuItem value="">
                                                                            Select comment
                                                                        </MenuItem>
                                                                        {commentOptions.map((option) => (
                                                                            <MenuItem key={option} value={option}>
                                                                                {option}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Box>

                                                                <Box
                                                                    className="print-status-text"
                                                                    sx={{
                                                                        display: 'none',
                                                                        '@media print': {
                                                                            display: 'block !important',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Typography variant="body2">
                                                                        {group.comments ?? '-'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (col.field === 'select') {
                                                        return (
                                                            <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                <Box className="print-hide">
                                                                    <Tooltip title={group.isCompleted ? 'Mark as incomplete' : 'Mark as completed'} arrow>
                                                                        <IconButton
                                                                            onClick={() => handleBottleGroupTickToggle(idx, true)}
                                                                            disabled={rows?.status === 1 || rows?.status === 3 || rows?.status === 4}
                                                                            size="small"
                                                                            sx={{
                                                                                color: group.isCompleted ? '#2e7d32' : '#9e9e9e',
                                                                                '&:hover': {
                                                                                    backgroundColor: group.isCompleted ? '#e8f5e8' : '#f5f5f5'
                                                                                }
                                                                            }}
                                                                        >
                                                                            {group.isCompleted ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                                <Box className="print-status-text" sx={{ display: 'none', '@media print': { display: 'block !important' } }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {group.isCompleted ? 'Completed' : 'Pending'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (col.field === 'action') {
                                                        return (
                                                            <TableCell className="print-hide" key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                                -
                                                            </TableCell>
                                                        );
                                                    }

                                                    return (
                                                        <TableCell key={col.field} sx={{ padding: '8px', minWidth: col.minWidth }}>
                                                            -
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                            {isExpanded && renderReadonlyTreeItems(group, 'Container', groupKey)}
                                        </Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </Box>
    );
};
